<template>
  <section class="t-left">
    <!-- Кнопка «Назад» над заголовком (конвенция проекта — button--card button--m):
         на шаге > 1 — к предыдущему шагу, иначе на предыдущую страницу. Если
         возвращаться некуда (прямой заход, разлогин) — на главную. -->
    <button class="button button--card button--m d-inline-flex flex-v-center gap-small mn-b-medium align-self-start" @click="goBack">
      <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" height="1em" width="1em"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 288 480 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-370.7 0 73.4-73.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-128 128z"></path></svg>
      {{ backLabel }}
    </button>

    <!-- Прогресс шагов: email → код → пароль (без пароля — email → код).
         Наверху страницы, над заголовком. -->
    <div class="d-flex gap-thin mn-b-big">
      <span
        v-for="(s, i) in steps"
        :key="s"
        class="signup-step"
        :class="{ 'signup-step--active': i <= currentStepIndex }"
      ></span>
    </div>

    <h3 class="mn-b-medium">{{ t('auth.signUp.title') }}</h3>

    <!-- Идентификатор пришёл в query: шлём код молча, форму не показываем. -->
    <div v-if="prefillLoading" class="w-100 d-flex flex-center flex-child-1 pd-big">
      <Loader :centered="false" />
    </div>

    <!-- ── Шаг 1: идентификатор (email / телефон) ───────────────────── -->
    <template v-else-if="step === 'identifier'">
      <!-- Tab selector -->
      <div v-if="availableTabs.length > 1" class="mn-b-small">
        <Tab v-model:selected="tabAuth" :tabs="availableTabs" classTabBar="w-100" />
      </div>

      <div class="pos-relative">
        <transition name="slide-fade">
          <div v-show="tabAuth === 'phone'" class="mn-b-small">
            <p class="footnote mn-b-medium opacity-50">{{ t('auth.signUp.smsNotice') }}</p>
            <FieldPhone
              :model-value="phone"
              @change="(event) => phone = event"
              :dropdownOptions="{ showDialCodeInSelection: true, showFlags: true, showDialCodeInList: true }"
              :validation="phoneValidation"
              mode="national"
              :inputOptions="{ placeholder: t('auth.signUp.phonePlaceholder') }"
              class="input mn-b-thin"
            />
          </div>
        </transition>
        <transition name="slide-fade">
          <div v-show="tabAuth === 'email'" class="mn-b-small">
            <p class="footnote mn-b-medium opacity-50">{{ t('auth.signUp.emailNotice') }}</p>
            <Field
              v-model:field="email"
              :placeholder="t('auth.signUp.emailPlaceholder')"
              :validation="emailValidation"
              class="input"
            />
          </div>
        </transition>
      </div>

      <!-- Без пароля шага пароля нет, и соглашение стоит здесь: код уже
           заводит аккаунт. -->
      <div v-if="!with_password" class="footnote mn-b-small" v-html="t('auth.signUp.agreement')"></div>

      <Button :submit="onSendCode" class="w-100 button--accent">
        {{ t('auth.signUp.sendCode') }}
      </Button>
    </template>

    <!-- ── Шаг 2: код из письма / SMS ───────────────────────────────── -->
    <template v-else-if="step === 'code'">
      <EnterCode
        v-model="code"
        :identifier="identifier"
        :is-email="tabAuth === 'email'"
        :submit="onVerifyCode"
        :resend="onResend"
      />
    </template>

    <!-- ── Шаг 3: пароль ────────────────────────────────────────────── -->
    <template v-else>
      <p class="footnote mn-b-medium opacity-50">{{ t('auth.enterPassword.registrationPasswordPrompt') }}</p>

      <div class="mn-b-small">
        <Field
          v-model:field="password"
          type="password"
          :placeholder="t('auth.enterPassword.passwordPlaceholder')"
          :validation="passwordValidation"
          class="input mn-b-thin"
        />
        <Field
          v-model:field="passwordRepeat"
          type="password"
          :placeholder="t('auth.enterPassword.repeatPasswordPlaceholder')"
          class="input"
        />
      </div>

      <!-- Соглашение: ссылка и название страницы — из локали, проект переопределяет
           через i18n override (auth.signUp.agreement). Не хардкодить путь в коде. -->
      <div class="footnote mn-b-small" v-html="t('auth.signUp.agreement')"></div>

      <Button :submit="onSubmit" class="w-100 button--accent mn-b-small">
        {{ t('auth.enterPassword.registerBtn') }}
      </Button>
    </template>

    <!-- Ссылка на вход — прибита к низу карточки -->
    <div class="w-100 mn-t-auto pd-t-medium">
      <router-link
        :to="{ name: 'Sign In', query: { returnUrl: route.query.returnUrl } }"
        class="underline d-block t-blue"
      >
        {{ t('auth.signUp.haveAccount') }}
      </router-link>
    </div>
  </section>
</template>

<script setup>
import Tab from '@martyrs/src/design-system/components/Tab/Tab.vue'
import Field from '@martyrs/src/design-system/components/Field/Field.vue'
import FieldPhone from '@martyrs/src/design-system/components/FieldPhone/FieldPhone.vue'
import Button from '@martyrs/src/design-system/components/Button/Button.vue'
import Loader from '@martyrs/src/design-system/components/Loader/Loader.vue'
import EnterCode from '@martyrs/src/modules/auth/views/components/blocks/EnterCode.vue'

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { useStore } from '@martyrs/src/modules/core/views/store/core.store.js'
import { setError } from '@martyrs/src/modules/core/views/store/core.store.js'
import { auth, setCookie } from '@martyrs/src/modules/auth/views/store/auth.js'
import $axios from '@martyrs/src/modules/core/views/utils/axios-instance.js'
import * as inputsValidation from '@martyrs/src/modules/auth/views/validations/inputs.validation'
import { resolveAuthBackTarget } from '@martyrs/src/modules/auth/views/utils/back-target.js'
import { resolveAuthMethods } from '@martyrs/src/modules/auth/views/utils/auth-methods.js'

const store = useStore()
const core = store.core || { state: {} }

const route = useRoute()
const router = useRouter()
const { t } = useI18n({ useScope: 'global' })

// Мастер: идентификатор → код → пароль. Пароль спрашиваем последним, а код
// приходит после ввода email. Сервер (/api/auth/signup) уже это поддерживает:
// первый запрос без пароля доходит до stepUp('signup') и высылает код, пароль
// нужен только на финальном создании аккаунта — поэтому весь флоу клиентский.
//
// Пароль выключен у идентификатора (`options.auth.email.password: false`): шага
// пароля нет. Код шлёт и проверяет вход по коду (`loginCode`,
// /api/auth/signin-code) — он же заводит аккаунт на новую почту, поэтому
// регистрация здесь и вход — один запрос.
const methods = resolveAuthMethods(core.state?.options?.auth)

const availableTabs = computed(() => Object.keys(methods).map(value => ({ name: t(`auth.signUp.${value}`), value })))

const tabAuth = ref(availableTabs.value.length ? availableTabs.value[0].value : '')

const with_password = computed(() => Boolean(methods[tabAuth.value]?.password))
const steps = computed(() => with_password.value ? ['identifier', 'code', 'password'] : ['identifier', 'code'])
const step = ref('identifier')
const currentStepIndex = computed(() => steps.value.indexOf(step.value))

const email = ref('')
const phone = ref('')
const code = ref('')
const password = ref('')
const passwordRepeat = ref('')
const challengeId = ref(null)

const phoneValidation = ref(null)
const emailValidation = ref(null)
const codeValidation = ref(null)
const passwordValidation = ref(null)

const identifier = computed(() => (tabAuth.value === 'email' ? email.value : phone.value))

// ── Идентификатор из query (?email= / ?phone=) ──────────────────────────────
// Человек уже назвал адрес раньше (квиз, лид-форма) — второй раз не спрашиваем:
// подставляем значение, переключаем вкладку и сразу шлём код. Значение читаем
// один раз в setup, до монтирования: FieldPhone берёт modelValue на своём mount.
const prefillLoading = ref(false)

function readPrefill() {
  const q = route.query
  // Email вперёд телефона: письмо дешевле SMS, и в квизе собираем именно его.
  for (const type of ['email', 'phone']) {
    const raw = typeof q[type] === 'string' ? q[type].trim() : ''
    if (!raw) continue
    if (!availableTabs.value.some(tab => tab.value === type)) continue
    return { type, value: raw }
  }
  return null
}

const prefill = readPrefill()

if (prefill) {
  tabAuth.value = prefill.type
  if (prefill.type === 'email') email.value = prefill.value
  else phone.value = prefill.value
  prefillLoading.value = true
}

onMounted(async () => {
  if (!prefill) return
  try {
    // silent — тост про ошибку показываем сами, с разбором дубля.
    await onSendCode({ silent: true })
  } catch (error) {
    // Адрес уже зарегистрирован — это не регистрация, а вход. Ведём на /signin
    // с тем же идентификатором и returnUrl, replace — чтобы «Назад» не вернул
    // на страницу, которая снова пошлёт код.
    if (error?.response?.data?.errorCode === 'USER_ALREADY_REGISTERED') {
      router.replace({ name: 'Sign In', query: { ...route.query } })
      return
    }
    // Ошибку входа по коду loginCode уже показал сам.
    if (with_password.value) setError(error)
  } finally {
    prefillLoading.value = false
  }
})

// Тело запроса входа по коду: только выбранный идентификатор.
function codePayload() {
  return {
    email: tabAuth.value === 'email' ? email.value : undefined,
    phone: tabAuth.value === 'phone' ? phone.value : undefined,
  }
}

// Тело запроса регистрации, общее для отправки кода и финального создания.
function basePayload() {
  return {
    email: tabAuth.value === 'email' ? email.value : undefined,
    phone: tabAuth.value === 'phone' ? phone.value : undefined,
    type: tabAuth.value,
    inviteCode: route.query.invite
  }
}

async function validateIdentifier() {
  if (tabAuth.value === 'phone') {
    await inputsValidation.validateInputs(
      phoneValidation, inputsValidation.validatePhone, phone.value, t('auth.validation.phone')
    )
  } else {
    await inputsValidation.validateInputs(
      emailValidation, inputsValidation.validateEmail, email.value, t('auth.validation.email')
    )
  }
}

// Шаг 1 → отправка кода. Первый запрос без пароля: сервер проверяет дубль и
// высылает OTP (status: requires_otp + challengeId).
// silent — не показывать тост самим: повторную отправку разбирает ResendCode
// (пауза между отправками для него не ошибка, а таймер).
async function onSendCode({ silent = false } = {}) {
  await validateIdentifier()
  try {
    // Без пароля — код входа: на новую почту он заведёт аккаунт, на занятую
    // впустит в неё. С паролем — код регистрации, занятая почта даёт ошибку.
    const result = with_password.value
      ? await $axios.post('/api/auth/signup', basePayload())
      : { data: await auth.actions.loginCode(codePayload(), tabAuth.value) }
    if (result.data?.status === 'requires_otp' && result.data.challenge?.challengeId) {
      challengeId.value = result.data.challenge.challengeId
      code.value = ''
      step.value = 'code'
    } else {
      // Сервер не запросил код — неожиданно для регистрации, показываем ошибку.
      throw new Error('OTP_NOT_REQUESTED')
    }
  } catch (error) {
    // Ошибку входа по коду loginCode уже показал сам (setError) — второй тост не нужен.
    if (!silent && with_password.value) setError(error)
    throw error
  }
}

// Повторная отправка кода — новый challenge. Ошибку отдаём наверх: ResendCode
// сам решит, таймер это или тост.
function onResend() {
  return onSendCode({ silent: true })
}

// Шаг 2 → проверяем, что код введён, и переходим к паролю. Фактическая
// верификация кода происходит на сервере при создании аккаунта (шаг 3).
// Без пароля шаг последний: код уходит во вход по коду, человек входит сразу.
async function onVerifyCode() {
  await inputsValidation.validateInputs(
    codeValidation,
    // validateInputs ждёт от валидатора объект { status, message }, а не boolean.
    (v) => ({ status: typeof v === 'string' && v.trim().length >= 4, message: t('auth.validation.codeFromEmail') }),
    code.value,
    t('auth.validation.codeFromEmail')
  )
  if (with_password.value) {
    step.value = 'password'
    return
  }
  try {
    // Кука, личность и ошибка — внутри loginCode, как при входе.
    const result = await auth.actions.loginCode({
      ...codePayload(),
      challengeId: challengeId.value,
      otp: code.value.trim(),
    }, tabAuth.value)
    redirectTo(result._id)
  } catch (error) {
    // Неверный код — очистить ячейки, человек остаётся на шаге кода.
    const codeErrors = ['INVALID_CODE', 'CODE_NOT_FOUND', 'ALREADY_USED']
    if (codeErrors.includes(error?.response?.data?.errorCode)) code.value = ''
    throw error
  }
}

// Куда вести после регистрации: returnUrl, иначе дефолт проекта, иначе профиль.
function redirectTo(user_id) {
  if (route.query.returnUrl) {
    // Прокидываем весь query (кроме returnUrl) на целевую страницу: флоу
    // вроде ?buyPack=… должны пережить регистрацию, а не теряться здесь.
    const { returnUrl, ...extraQuery } = route.query
    router.push({ path: returnUrl, query: { ...extraQuery, afterAuth: 'true' } })
  } else {
    // Дефолт пост-регистрации — из конфига проекта (options.auth.redirectAfterAuth),
    // иначе профиль пользователя (дефолт martyrs).
    const configured = core.state?.options?.auth?.redirectAfterAuth
    if (configured) {
      router.push({ path: configured, query: { afterAuth: 'true' } })
    } else {
      router.push({ name: 'User Profile', params: { _id: user_id }, query: { afterAuth: 'true' } })
    }
  }
}

// Шаг 3 → создание аккаунта: код + пароль уходят вместе, сервер верифицирует OTP
// и создаёт пользователя. Неверный код возвращает на шаг ввода кода.
async function onSubmit() {
  await inputsValidation.validateInputs(
    passwordValidation, inputsValidation.validatePassword, password.value, t('auth.validation.password')
  )
  if (password.value !== passwordRepeat.value) {
    setError({ response: { data: { errorCode: 'PASSWORDS_DO_NOT_MATCH' } } })
    throw new Error('PASSWORDS_DO_NOT_MATCH')
  }

  try {
    const result = await $axios.post('/api/auth/signup', {
      ...basePayload(),
      password: password.value,
      challengeId: challengeId.value,
      otp: code.value.trim()
    })

    if (result.data?.accessToken) {
      // Кука — через хранилище, с теми же атрибутами (домен, secure), что при
      // входе: своя кука без домена не стиралась ни на 401, ни при выходе.
      await setCookie('user', result.data)
      await auth.actions.initialize()

      redirectTo(result.data._id)
    }
  } catch (error) {
    setError(error)
    // Проблема с кодом — вернуть пользователя на шаг ввода кода.
    const codeErrors = ['INVALID_CODE', 'CODE_NOT_FOUND', 'ALREADY_USED']
    if (codeErrors.includes(error?.response?.data?.errorCode)) {
      step.value = 'code'
    }
    throw error
  }
}

// Куда ведёт кнопка с первого шага. Считаем один раз при открытии страницы:
// история после монтирования компонента уже не меняется.
const backTarget = resolveAuthBackTarget(router)

const backLabel = computed(() => {
  const isFirstStep = steps.value.indexOf(step.value) === 0
  return isFirstStep && backTarget.isHome ? t('auth.signUp.home') : t('auth.signUp.back')
})

// «Назад»: на шаге > 1 — предыдущий шаг, на первом — на предыдущую страницу
// (или на главную, если возвращаться некуда).
function goBack() {
  const i = steps.value.indexOf(step.value)
  if (i > 0) step.value = steps.value[i - 1]
  else if (backTarget.isHome) router.push(backTarget.path)
  else router.back()
}
</script>

<style lang="scss">
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  position: absolute;
  transform: translateX(20px);
  opacity: 0;
  left: 0;
  top: 0;
}

.signup-step {
  height: 4px;
  flex: 1;
  border-radius: var(--radius-pill);
  background: rgba(var(--bg-inverse), 0.12);
  transition: background 0.3s ease;

  &--active {
    background: rgb(var(--color-accent));
  }
}
</style>
